import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { logError } from "@/lib/logger";
import { Gift, ExternalLink, Heart, Calendar, ShoppingBag, Sparkles } from "lucide-react";
import { EditableText as T } from "@/components/live/EditableText";
import { EditableList } from "@/components/live/EditableList";
import { LinkEditorDialog } from "@/components/live/LinkEditorDialog";
import { WishlistItemDialog } from "@/components/live/WishlistItemDialog";
import { LiveItemControls, LiveAddButton, useLiveDelete } from "@/components/live/LiveItemControls";
import { useContent, resolveUrl } from "@/lib/content";
import { Section } from "@/components/live/Section";

const Gifts = () => {
  const [wishlistLinks, setWishlistLinks] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { editMode } = useContent();
  const liveDelete = useLiveDelete();
  const [linkDialog, setLinkDialog] = useState({ open: false, link: null });
  const [itemDialog, setItemDialog] = useState({ open: false, item: null });

  const fetchWishlistLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/links?category=wishlist`);
      setWishlistLinks(response.data || []);
    } catch (error) {
      logError("Failed to fetch wishlist links:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWishlistItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/wishlist`);
      setWishlistItems(response.data || []);
    } catch (error) {
      logError("Failed to fetch wishlist items:", error);
    }
  }, []);

  useEffect(() => {
    fetchWishlistLinks();
    fetchWishlistItems();
  }, [fetchWishlistLinks, fetchWishlistItems]);

  const favoriteActivities = [
    "Day Spa Trips",
    "Beach Days",
    "Massages",
    "Art Museums/Exhibits",
    "Historical Museums",
    "Horse Races",
    "Football Games",
    "Basketball Games",
    "Arcades",
    "Pool Halls",
    "Casinos",
    "Live Concerts/Festivals",
    "Dining Out (Favorite Cuisines: Italian, Mexican, Steakhouses, Seafood)",
    "Shopping (OF COURSE!)"
  ];

  const favoriteGifts = [
    "Flowers (Roses, Orchids, Lilies)",
    "Starbucks (Vanilla Sweet Cream Cold Brew)",
    'Perfume (Oscar de la Renta "Alibi", Gucci "Flora Gorgeous Gardenia", Carolina Herrera "Good Girl")',
    "Fresh Fruit (Strawberries, Bananas, Mangoes, Pineapples, Oranges)",
    "Sweets (Tiramisu, Snickerdoodles, Gourmet Chocolates, Cheesecake)",
    "Gift Cards (Honey Birdette, United Airline, Delta Airline, Airbnb, Hotels.com, Ulta, Sephora, Nordstroms, Neiman Marcus, Amazon, Ruth's Chris, Maestro's, Local Day Spa, etc)",
    "Champagne (Veuve Clicquot, GH Mumm, Dom Perignon)"
  ];

  const sizes = [
    { label: "Bra", size: "34B" },
    { label: "Panty", size: "XXS preferred, or XS" },
    { label: "Top", size: "XXS/XS" },
    { label: "Bottom", size: "00/0 or XXS/XS" },
    { label: "Dress", size: "00/0 or XXS/XS" },
    { label: "Shoe", size: "8" },
    { label: "Heel", size: "8.5/9" }
  ];

  return (
    <div data-testid="gifts-page">
      {/* Header */}
      <section className="page-header" data-testid="gifts-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="gifts.a-thoughtful-gesture" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">A Thoughtful Gesture</T>
          <T k="gifts.spoil-me" as="h1" className="page-title animate-fade-in stagger-1">Spoil Me</T>
          <T k="gifts.while-never-expected" as="p" className="page-subtitle animate-fade-in stagger-2">While never expected, thoughtful gifts are always appreciated and remembered fondly.</T>
        </div>
      </section>

      {/* Quality Time Section */}
      <Section k="gifts.section.quality-time-section" label="quality time" className="py-16 px-6 md:px-12 lg:px-24" data-testid="quality-time-section">
        <div className="max-w-4xl mx-auto">
          <div className="glass rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-jade-soft rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-jade-text" />
              </div>
              <T k="gifts.quality-time" as="h2" className="font-heading text-3xl text-charcoal">Quality Time</T>
            </div>
            <T k="gifts.quality-intro" as="p" className="font-body text-lg text-stone-grey leading-relaxed mb-8">Quality Time is my #1 Love Language! Want to plan an eventful date together? Some of my FAVORITE activities include:</T>
            <EditableList k="gifts.activities" items={favoriteActivities} newItem="New activity" addLabel="Add activity" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(activity, index, Field) => (
                <div className="flex items-center gap-3" data-testid={`activity-${index}`}>
                  <div className="w-2 h-2 bg-champagne-gold rounded-full flex-shrink-0" />
                  <Field className="font-body text-stone-grey" />
                </div>
              )}
            </EditableList>
          </div>
        </div>
      </Section>

      {/* Favorite Gifts Section */}
      <Section k="gifts.section.favorite-gifts-section" label="favorite gifts" className="py-16 px-6 md:px-12 lg:px-24 bg-blush-pink/20" data-testid="favorite-gifts-section">
        <div className="max-w-4xl mx-auto">
          <div className="glass rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-blush-pink rounded-full flex items-center justify-center">
                <Gift className="w-6 h-6 text-lipstick-red" />
              </div>
              <T k="gifts.receiving-gifts" as="h2" className="font-heading text-3xl text-charcoal">Receiving Gifts</T>
            </div>
            <T k="gifts.one-of-my" as="p" multiline className="font-body text-lg text-stone-grey leading-relaxed mb-8">One of my main love languages aside from quality time, is receiving gifts! Feeling generous but not sure what to give? Here are some of my favorites below!</T>
            <EditableList k="gifts.favorites" items={favoriteGifts} newItem="A new favorite gift" addLabel="Add gift idea" className="space-y-3">
              {(gift, index, Field) => (
                <div className="flex items-start gap-3" data-testid={`gift-${index}`}>
                  <Heart className="w-4 h-4 text-champagne-gold flex-shrink-0 mt-1" />
                  <Field multiline className="font-body text-stone-grey" />
                </div>
              )}
            </EditableList>
          </div>
        </div>
      </Section>

      {/* Sizes Section */}
      <Section k="gifts.section.sizes-section" label="sizes" className="py-16 px-6 md:px-12 lg:px-24" data-testid="sizes-section">
        <div className="max-w-4xl mx-auto">
          <div className="glass rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-champagne/30 rounded-full flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-champagne-gold" />
              </div>
              <T k="gifts.dress-me-up" as="h2" className="font-heading text-3xl text-charcoal">Dress Me Up</T>
            </div>
            <T k="gifts.want-to-dress" as="p" className="font-body text-lg text-stone-grey leading-relaxed mb-8">Want to dress me up like your personal Barbie Doll? See my sizes below!</T>
            <EditableList k="gifts.sizes" items={sizes} newItem={{ label: "Item", size: "Size" }} addLabel="Add size" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {(item, index, Field) => (
                <div className="bg-white rounded-xl p-4 text-center shadow-sm" data-testid={`size-${index}`}>
                  <Field field="label" as="p" className="font-body text-xs tracking-widest uppercase text-taupe mb-1" />
                  <Field field="size" as="p" className="font-heading text-lg text-charcoal" />
                </div>
              )}
            </EditableList>
          </div>
        </div>
      </Section>

      {/* Curated Wishlist Items (managed via admin "Spoil Me") */}
      {(wishlistItems.length > 0 || editMode) && (
        <Section k="gifts.section.wishlist-items-section" label="wishlist items" className="py-16 px-6 md:px-12 lg:px-24" data-testid="wishlist-items-section">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-10">
              <Heart className="w-6 h-6 text-champagne-gold" />
              <T k="gifts.a-few-of" as="h2" className="font-heading text-3xl text-charcoal text-center">A Few of My Favorites</T>
              <Heart className="w-6 h-6 text-champagne-gold" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {wishlistItems.map((item, index) => (
                <div
                  key={item.id || index}
                  className="glass rounded-2xl overflow-hidden flex flex-col relative"
                  data-testid={`wishlist-item-${index}`}
                >
                  <LiveItemControls id={item.id} onEdit={() => setItemDialog({ open: true, item })} onDelete={() => liveDelete(`wishlist/${item.id}`, item.name, fetchWishlistItems)} />
                  {item.image_url && (
                    <div className="aspect-[4/3] overflow-hidden bg-sand/30">
                      <img
                        src={resolveUrl(item.image_url)}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-heading text-lg text-charcoal">{item.name}</h3>
                      {item.price_range && (
                        <span className="font-body text-xs text-champagne-gold whitespace-nowrap mt-1">{item.price_range}</span>
                      )}
                    </div>
                    {item.description && (
                      <p className="font-body text-sm text-stone-grey leading-relaxed mb-4 flex-1">{item.description}</p>
                    )}
                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary inline-flex items-center justify-center gap-2 mt-auto"
                        data-testid={`wishlist-item-link-${index}`}
                      >
                        <ShoppingBag className="w-4 h-4" />
                        View Gift
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <LiveAddButton label="Add a gift" testId="live-add-wishlist-item" onClick={() => setItemDialog({ open: true, item: null })} />
            </div>
          </div>
        </Section>
      )}

      {/* External Wishlists */}
      <Section k="gifts.section.external-wishlists" label="external wishlists" className="py-16 px-6 md:px-12 lg:px-24 bg-sand/30" data-testid="external-wishlists">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Sparkles className="w-6 h-6 text-champagne-gold" />
            <T k="gifts.full-wishlists" as="h2" className="font-heading text-3xl text-charcoal">Full Wishlists</T>
            <Sparkles className="w-6 h-6 text-champagne-gold" />
          </div>
          <T k="gifts.browse-my-complete" as="p" className="font-body text-stone-grey mb-8">Browse my complete wishlists for more gift ideas:</T>
          <div className="flex flex-wrap justify-center gap-4">
            {wishlistLinks.length > 0 ? (
              wishlistLinks.map((link, index) => (
                <a
                  key={link.id || index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2 relative"
                  data-testid={`wishlist-link-${index}`}
                >
                  <LiveItemControls id={link.id} className="-top-3 -right-3" onEdit={() => setLinkDialog({ open: true, link })} onDelete={() => liveDelete(`links/${link.id}`, link.name, fetchWishlistLinks)} />
                  <span className="text-lg">{link.icon || "🎁"}</span>
                  {link.name}
                </a>
              ))
            ) : (
              <>
                <a
                  href="https://youpay.me/kalaniluana"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="youpay-wishlist-link"
                >
                  <Gift className="w-4 h-4" />
                  YouPay Wishlist
                </a>
                <a
                  href="https://www.luxylist.it/kalaniluana"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="luxylist-wishlist-link"
                >
                  <Gift className="w-4 h-4" />
                  LuxyList Wishlist
                </a>
                <a
                  href="https://throne.com/kalaniluana"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="throne-wishlist-link"
                >
                  <Gift className="w-4 h-4" />
                  Throne Wishlist
                </a>
              </>
            )}
            <LiveAddButton label="Add wishlist link" testId="live-add-wishlist-link" onClick={() => setLinkDialog({ open: true, link: null })} />
          </div>
        </div>
      </Section>

      {/* Note */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <div className="glass rounded-xl p-8 text-center">
            <T k="gifts.reminder" as="p" multiline className="font-body text-stone-grey leading-relaxed">A gentle reminder: Gifts are expressions of appreciation, never obligations. Your presence and the quality of our connection are what I value most. If you'd like to send something, please reach out for shipping details.</T>
            <T k="gifts.with-gratitude-kalani" as="p" className="font-accent text-2xl text-champagne-gold mt-6">With gratitude, Kalani</T>
          </div>
        </div>
      </section>
      <LinkEditorDialog open={linkDialog.open} link={linkDialog.link} fixedCategory="wishlist" onClose={() => setLinkDialog({ open: false, link: null })} onSaved={fetchWishlistLinks} />
      <WishlistItemDialog open={itemDialog.open} item={itemDialog.item} onClose={() => setItemDialog({ open: false, item: null })} onSaved={fetchWishlistItems} />
    </div>
  );
};

export default Gifts;
